import { useConnection } from '@/features/connections/connection-context';
import { useRunFirestoreQuery, useValidateFirestoreQueryPreview } from '@/query/firebase';
import { DEFAULT_QUERY_LIMIT } from '@shared/constants';
import { Filter, FirestoreQuery, OrderBy } from '@shared/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PersistedQueryState } from '../../tabs-storage';
import { saveQueryState } from '../../tabs-storage';
import {
  buildFirestoreQuery,
  getDefaultState,
  type QueryState,
  stateReducer,
} from './query-builder-store.domain';

const QUERY_ERROR_HEADER = 'Query failed. Details:\n\n';

function formatQueryErrorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : 'Failed to execute query';
  return message.startsWith(QUERY_ERROR_HEADER) ? message : `${QUERY_ERROR_HEADER}${message}`;
}

export interface QueryBuilderContextType {
  getState: (collectionId: string) => QueryState;
  addCondition: (collectionId: string) => void;
  removeCondition: (collectionId: string, index: number) => void;
  updateCondition: (collectionId: string, index: number, condition: Filter) => void;
  addOrCondition: (collectionId: string) => void;
  removeOrCondition: (collectionId: string, index: number) => void;
  updateOrCondition: (collectionId: string, index: number, condition: Filter) => void;
  addOrderBy: (collectionId: string) => void;
  removeOrderBy: (collectionId: string, index: number) => void;
  updateOrderBy: (collectionId: string, index: number, orderBy: OrderBy) => void;
  setSingleOrderBy: (collectionId: string, orderBy: OrderBy | null) => void;
  setListOrderBy: (collectionId: string, orderBy: OrderBy | null) => void;
  setLimit: (collectionId: string, limit: number) => void;
  setDocumentIdPrefix: (collectionId: string, value: string) => void;
  clearQuery: (collectionId: string) => void;
  executeTestRun: (connectionId: string, collectionName: string) => Promise<void>;
  executeQuery: (connectionId: string, collectionName: string) => Promise<void>;
  executeQueryWith: (
    connectionId: string,
    collectionName: string,
    query: FirestoreQuery
  ) => Promise<void>;
  executeQueryLoadMore: (connectionId: string, collectionName: string) => Promise<void>;
  reset: (collectionId: string) => void;
  clearAll: () => void;
  hydrateCollection: (collectionId: string, state: PersistedQueryState) => void;
  applyQueryToCollection: (collectionId: string, query: FirestoreQuery) => void;
}

export function useQueryBuilderStore(): QueryBuilderContextType {
  const { connectionId } = useConnection();
  const runFirestoreQueryMutation = useRunFirestoreQuery();
  const validateQueryPreviewMutation = useValidateFirestoreQueryPreview();
  const [queriesByCollection, setQueriesByCollection] = useState<Record<string, QueryState>>({});
  const queriesByCollectionRef = useRef(queriesByCollection);
  queriesByCollectionRef.current = queriesByCollection;
  const testRunIdRef = useRef(0);

  useEffect(() => {
    if (!connectionId) return;
    Object.entries(queriesByCollection).forEach(([collectionId, state]) => {
      saveQueryState(connectionId, collectionId, {
        conditions: state.conditions,
        orConditions: state.orConditions,
        orderBy: state.orderBy,
        limit: state.limit,
        documentIdPrefix: state.documentIdPrefix,
        listOrderBy: state.listOrderBy ?? null,
      });
    });
  }, [connectionId, queriesByCollection]);

  const getState = useCallback(
    (collectionId: string): QueryState => {
      return queriesByCollection[collectionId] ?? getDefaultState();
    },
    [queriesByCollection]
  );

  const updateState = useCallback((collectionId: string, patch: Partial<QueryState>) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      const next = stateReducer(current, patch);
      const changed = (Object.keys(patch) as (keyof QueryState)[]).some(
        (k) => current[k as keyof QueryState] !== next[k as keyof QueryState]
      );
      if (!changed) return prev;
      return { ...prev, [collectionId]: next };
    });
  }, []);

  const addCondition = useCallback((collectionId: string) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      return {
        ...prev,
        [collectionId]: stateReducer(current, {
          conditions: [...current.conditions, { field: '', operator: '==', value: '' }],
        }),
      };
    });
  }, []);

  const removeCondition = useCallback((collectionId: string, index: number) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      return {
        ...prev,
        [collectionId]: stateReducer(current, {
          conditions: current.conditions.filter((_, i) => i !== index),
        }),
      };
    });
  }, []);

  const updateCondition = useCallback((collectionId: string, index: number, condition: Filter) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      const updated = [...current.conditions];
      updated[index] = condition;
      return { ...prev, [collectionId]: stateReducer(current, { conditions: updated }) };
    });
  }, []);

  const addOrCondition = useCallback((collectionId: string) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      return {
        ...prev,
        [collectionId]: stateReducer(current, {
          orConditions: [...current.orConditions, { field: '', operator: '==', value: '' }],
        }),
      };
    });
  }, []);

  const removeOrCondition = useCallback((collectionId: string, index: number) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      return {
        ...prev,
        [collectionId]: stateReducer(current, {
          orConditions: current.orConditions.filter((_, i) => i !== index),
        }),
      };
    });
  }, []);

  const updateOrCondition = useCallback(
    (collectionId: string, index: number, condition: Filter) => {
      setQueriesByCollection((prev) => {
        const current = prev[collectionId] ?? getDefaultState();
        const updated = [...current.orConditions];
        updated[index] = condition;
        return { ...prev, [collectionId]: stateReducer(current, { orConditions: updated }) };
      });
    },
    []
  );

  const addOrderBy = useCallback((collectionId: string) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      return {
        ...prev,
        [collectionId]: stateReducer(current, {
          orderBy: [...current.orderBy, { field: '', direction: 'asc' as const }],
        }),
      };
    });
  }, []);

  const removeOrderBy = useCallback((collectionId: string, index: number) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      return {
        ...prev,
        [collectionId]: stateReducer(current, {
          orderBy: current.orderBy.filter((_, i) => i !== index),
        }),
      };
    });
  }, []);

  const updateOrderBy = useCallback((collectionId: string, index: number, orderByItem: OrderBy) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      const updated = [...current.orderBy];
      updated[index] = orderByItem;
      return { ...prev, [collectionId]: stateReducer(current, { orderBy: updated }) };
    });
  }, []);

  const setSingleOrderBy = useCallback((collectionId: string, orderByItem: OrderBy | null) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      return {
        ...prev,
        [collectionId]: stateReducer(current, {
          orderBy: orderByItem ? [orderByItem] : [],
        }),
      };
    });
  }, []);

  const setListOrderBy = useCallback((collectionId: string, orderBy: OrderBy | null) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      return {
        ...prev,
        [collectionId]: stateReducer(current, { listOrderBy: orderBy }),
      };
    });
  }, []);

  const setLimit = useCallback(
    (collectionId: string, newLimit: number) => {
      updateState(collectionId, { limit: newLimit });
    },
    [updateState]
  );

  const setDocumentIdPrefix = useCallback(
    (collectionId: string, value: string) => {
      updateState(collectionId, { documentIdPrefix: value });
    },
    [updateState]
  );

  const clearQuery = useCallback(
    (collectionId: string) => {
      testRunIdRef.current += 1;
      setQueriesByCollection((prev) => ({
        ...prev,
        [collectionId]: getDefaultState(),
      }));
    },
    []
  );

  const executeTestRun = useCallback(
    async (connectionId: string, collectionName: string) => {
      if (!connectionId) return;
      const state = queriesByCollectionRef.current[collectionName] ?? getDefaultState();
      if (!state) return;

      const runId = (testRunIdRef.current += 1);

      setQueriesByCollection((prev) => {
        const current = prev[collectionName] ?? getDefaultState();
        return {
          ...prev,
          [collectionName]: stateReducer(current, {
            testRunLoading: true,
            testRunError: null,
          }),
        };
      });

      const query = buildFirestoreQuery(
        { ...state, limit: 1 },
        collectionName
      );

      try {
        await validateQueryPreviewMutation.mutateAsync({
          connectionId,
          collectionName,
          query,
        });
        if (runId !== testRunIdRef.current) return;
        setQueriesByCollection((prev) => {
          const current = prev[collectionName] ?? getDefaultState();
          return {
            ...prev,
            [collectionName]: stateReducer(current, {
              testRunLoading: false,
              testRunError: null,
            }),
          };
        });
      } catch (err) {
        const errorMessage = formatQueryErrorMessage(err);
        if (runId !== testRunIdRef.current) return;
        setQueriesByCollection((prev) => {
          const current = prev[collectionName] ?? getDefaultState();
          return {
            ...prev,
            [collectionName]: stateReducer(current, {
              testRunLoading: false,
              testRunError: errorMessage,
            }),
          };
        });
      }
    },
    [validateQueryPreviewMutation]
  );

  const executeQuery = useCallback(
    async (connectionId: string, collectionName: string) => {
      if (!connectionId) return;

      setQueriesByCollection((prev) => {
        const current = prev[collectionName] ?? getDefaultState();
        return {
          ...prev,
          [collectionName]: stateReducer(current, {
            error: null,
            loading: true,
            loadingMore: false,
          }),
        };
      });

      const state = queriesByCollectionRef.current[collectionName] ?? getDefaultState();
      const query = buildFirestoreQuery(state, collectionName);

      try {
        const results = await runFirestoreQueryMutation.mutateAsync({
          connectionId,
          collectionName,
          query,
        });
        setQueriesByCollection((prev) => {
          const current = prev[collectionName] ?? getDefaultState();
          return {
            ...prev,
            [collectionName]: stateReducer(current, {
              activeQuery: query,
              queryResults: results,
              loading: false,
              error: null,
            }),
          };
        });
      } catch (err) {
        const errorMessage = formatQueryErrorMessage(err);
        setQueriesByCollection((prev) => {
          const current = prev[collectionName] ?? getDefaultState();
          return {
            ...prev,
            [collectionName]: stateReducer(current, {
              loading: false,
              error: errorMessage,
              queryResults: null,
            }),
          };
        });
      }
    },
    [runFirestoreQueryMutation]
  );

  const executeQueryLoadMore = useCallback(
    async (connectionId: string, collectionName: string) => {
      if (!connectionId) return;
      const state = queriesByCollectionRef.current[collectionName] ?? getDefaultState();
      const { activeQuery, queryResults } = state;
      if (
        !activeQuery ||
        !queryResults?.documents?.length ||
        queryResults.hasMore !== true
      ) {
        return;
      }
      const lastDoc = queryResults.documents[queryResults.documents.length - 1];
      setQueriesByCollection((prev) => {
        const current = prev[collectionName] ?? getDefaultState();
        return {
          ...prev,
          [collectionName]: stateReducer(current, { loadingMore: true, error: null }),
        };
      });
      try {
        const queryWithCursor = { ...activeQuery, startAfter: lastDoc };
        const nextResults = await runFirestoreQueryMutation.mutateAsync({
          connectionId,
          collectionName,
          query: queryWithCursor,
        });
        setQueriesByCollection((prev) => {
          const current = prev[collectionName] ?? getDefaultState();
          const prevResults = current.queryResults;
          if (!prevResults) return prev;
          const merged: typeof prevResults = {
            documents: [...prevResults.documents, ...nextResults.documents],
            meta: nextResults.meta,
            hasMore: nextResults.hasMore ?? false,
          };
          return {
            ...prev,
            [collectionName]: stateReducer(current, {
              queryResults: merged,
              loadingMore: false,
            }),
          };
        });
      } catch (err) {
        const errorMessage = formatQueryErrorMessage(err);
        setQueriesByCollection((prev) => {
          const current = prev[collectionName] ?? getDefaultState();
          return {
            ...prev,
            [collectionName]: stateReducer(current, {
              loadingMore: false,
              error: errorMessage,
            }),
          };
        });
      }
    },
    [runFirestoreQueryMutation]
  );

  const executeQueryWith = useCallback(
    async (
      connectionId: string,
      collectionName: string,
      query: FirestoreQuery
    ): Promise<void> => {
      if (!connectionId) return;

      setQueriesByCollection((prev) => {
        const current = prev[collectionName] ?? getDefaultState();
        return {
          ...prev,
          [collectionName]: stateReducer(current, {
            error: null,
            loading: true,
          }),
        };
      });

      try {
        const results = await runFirestoreQueryMutation.mutateAsync({
          connectionId,
          collectionName,
          query,
        });
        setQueriesByCollection((prev) => {
          const current = prev[collectionName] ?? getDefaultState();
          return {
            ...prev,
            [collectionName]: stateReducer(current, {
              activeQuery: query,
              queryResults: results,
              loading: false,
              error: null,
            }),
          };
        });
      } catch (err) {
        const errorMessage = formatQueryErrorMessage(err);
        setQueriesByCollection((prev) => {
          const current = prev[collectionName] ?? getDefaultState();
          return {
            ...prev,
            [collectionName]: stateReducer(current, {
              loading: false,
              error: errorMessage,
              queryResults: null,
            }),
          };
        });
      }
    },
    [runFirestoreQueryMutation]
  );

  const reset = useCallback((collectionId: string) => {
    setQueriesByCollection((prev) => ({
      ...prev,
      [collectionId]: getDefaultState(),
    }));
  }, []);

  const clearAll = useCallback(() => {
    setQueriesByCollection({});
  }, []);

  const hydrateCollection = useCallback((collectionId: string, persisted: PersistedQueryState) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      return {
        ...prev,
        [collectionId]: stateReducer(current, {
          conditions: persisted.conditions ?? [],
          orConditions: persisted.orConditions ?? [],
          orderBy: persisted.orderBy ?? [],
          limit: persisted.limit ?? DEFAULT_QUERY_LIMIT,
          documentIdPrefix: persisted.documentIdPrefix ?? '',
          listOrderBy: persisted.listOrderBy ?? null,
        }),
      };
    });
  }, []);

  const applyQueryToCollection = useCallback((collectionId: string, query: FirestoreQuery) => {
    setQueriesByCollection((prev) => {
      const current = prev[collectionId] ?? getDefaultState();
      return {
        ...prev,
        [collectionId]: stateReducer(current, {
          conditions: query.filters ?? [],
          orConditions: query.orGroups?.[0] ?? [],
          orderBy: query.orderBy ?? [],
          limit: query.limit ?? DEFAULT_QUERY_LIMIT,
          documentIdPrefix: query.documentIdPrefix ?? '',
        }),
      };
    });
  }, []);

  return {
    getState,
    addCondition,
    removeCondition,
    updateCondition,
    addOrCondition,
    removeOrCondition,
    updateOrCondition,
    addOrderBy,
    removeOrderBy,
    updateOrderBy,
    setSingleOrderBy,
    setListOrderBy,
    setLimit,
    setDocumentIdPrefix,
    clearQuery,
    executeTestRun,
    executeQuery,
    executeQueryWith,
    executeQueryLoadMore,
    reset,
    clearAll,
    hydrateCollection,
    applyQueryToCollection,
  };
}
